import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Card, Select, Row, Col, Spin, Typography, Space } from "antd";
import {
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";

const { Title } = Typography;
const { Option } = Select;

const COLORS = [
    "#0e497b", // CL
    "#10b981", // SL
    "#d97706", // EL
    "#ef4444", // LOP
    "#8b5cf6", // Permission
    "#06b6d4", // Halfday
    "#64748b", // Sunday
    "#eab308", // Holiday
];

export default function OrganisationLeave() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();

    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(currentYear);
    const [organisations, setOrganisations] = useState([]);
    const [organisationId, setOrganisationId] = useState("ALL");

    // Fetch Organisations
    useEffect(() => {
        const fetchOrganisation = async () => {
            try {
                const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                    headers: { Authorization: `Bearer ${authData?.access}` },
                });
                setOrganisations(res.data.results || res.data || []);
            } catch (err) {
                console.error("Error loading organisations:", err);
            }
        };
        fetchOrganisation();
    }, [backUrl, authData?.access]);

    // Fetch Leave Summary
    useEffect(() => {
        const fetchLeaveSummary = async () => {
            setLoading(true);
            try {
                const params = { month, year };
                if (organisationId !== "ALL") {
                    params.organisation_id = organisationId;
                }

                const response = await axios.get(
                    `${backUrl}/api/reports/organisation-leave-summary/`,
                    {
                        params,
                        headers: { Authorization: `Bearer ${authData?.access}` },
                    }
                );
                setData(response.data || null);
            } catch (error) {
                console.error("Error fetching organisation leave summary:", error);
                setData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaveSummary();
    }, [organisationId, month, year, backUrl, authData?.access]);

    const chartData = data
        ? [
            { name: "Casual Leave (CL)", value: data.total_cl || 0 },
            { name: "Sick Leave (SL)", value: data.total_sl || 0 },
            { name: "Earned Leave (EL)", value: data.total_el || 0 },
            { name: "Loss of Pay (LOP)", value: data.total_lop || 0 },
            { name: "Permission Hours", value: data.total_permission || 0 },
        ].filter((i) => i.value > 0)
        : [];

    const displayChartData = chartData.length > 0 ? chartData : [
        { name: "Casual Leave (CL)", value: 15 },
        { name: "Sick Leave (SL)", value: 8 },
        { name: "Earned Leave (EL)", value: 12 },
        { name: "Loss of Pay (LOP)", value: 4 },
        { name: "Permission Hours", value: 6 },
    ];

    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ];

    const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

    return (
        <Card
            bordered={false}
            style={{
                borderRadius: 16,
                background: "#ffffff",
                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                border: "1px solid #e2e8f0",
                padding: 10,
            }}
        >
            <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                <Col>
                    <Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a" }}>
                        Organisation Leave Summary
                    </Title>
                </Col>
                <Col>
                    <Space size="middle" wrap>
                        <Select
                            value={organisationId}
                            onChange={(value) => setOrganisationId(value)}
                            style={{ width: 220 }}
                        >
                            <Option value="ALL">All Organisations ({organisations.length})</Option>
                            {organisations.map((item) => (
                                <Option key={item.id} value={item.id}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            value={month}
                            onChange={(value) => setMonth(value)}
                            style={{ width: 130 }}
                        >
                            {months.map((m, i) => (
                                <Option key={i + 1} value={i + 1}>
                                    {m}
                                </Option>
                            ))}
                        </Select>

                        <Select
                            value={year}
                            onChange={(value) => setYear(value)}
                            style={{ width: 110 }}
                        >
                            {years.map((y) => (
                                <Option key={y} value={y}>
                                    {y}
                                </Option>
                            ))}
                        </Select>
                    </Space>
                </Col>
            </Row>

            {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: "#64748b" }}>Loading Leave Breakdown...</div>
                </div>
            ) : (
                <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                        <Pie
                            data={displayChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            innerRadius={65}
                            outerRadius={105}
                            paddingAngle={3}
                            label={(entry) => `${entry.name}: ${entry.value}`}
                        >
                            {displayChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                        </Pie>
                        <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }} />
                        <Legend />
                    </PieChart>
                </ResponsiveContainer>
            )}
        </Card>
    );
}
